import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Eye, Mail, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import EmailPreviewModal from '../shared/EmailPreviewModal';
import { buildProposalHTML } from '../../utils/printProposal';
import './Proposals.css';

const emptyForm = {
  recipientType: 'lead',
  clientId: '',
  leadId: '',
  clientName: '',
  clientEmail: '',
  clientCompany: '',
  title: '',
  preparedBy: '',
  executiveSummary: '',
  scopeIncludedText: '',
  scopeExcludedText: '',
  deliverablesText: '',
  timelineText: '',
  assumptionsText: '',
  termsPaymentSchedule: '',
  termsRevisionPolicy: '',
  termsValidity: '',
  termsNextSteps: '',
  estimateTemplateKey: '',
  contentTemplateKey: '',
  discount: 0,
  items: [],
  notes: '',
  expiresAt: ''
};

const emptyTemplateForm = {
  key: '',
  name: '',
  description: '',
  title: '',
  executiveSummary: '',
  scopeIncludedText: '',
  scopeExcludedText: '',
  deliverablesText: '',
  timelineText: '',
  assumptionsText: '',
  termsPaymentSchedule: '',
  termsRevisionPolicy: '',
  termsValidity: '',
  termsNextSteps: '',
  isActive: true
};

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toLines(text) {
  return String(text || '').split('\n').map((line) => line.trim()).filter(Boolean);
}

function timelineToText(timeline = []) {
  if (!Array.isArray(timeline)) return '';
  return timeline
    .map((t) => [t?.phase || '', t?.duration || '', t?.details || ''].join(' | '))
    .join('\n');
}

function textToTimeline(text) {
  return toLines(text).map((line) => {
    const [phase = '', duration = '', ...rest] = line.split('|').map((part) => part.trim());
    return { phase, duration, details: rest.join(' | ').trim() };
  }).filter((row) => row.phase || row.duration || row.details);
}

function getLatestDeclineNote(notes) {
  const text = String(notes || '');
  const pattern = /Client decline note \(([^)]+)\):\s*([\s\S]*?)(?=\n{2}Client decline note \(|$)/g;
  let match;
  let latest = null;
  while ((match = pattern.exec(text)) !== null) {
    latest = {
      when: match[1] || '',
      reason: String(match[2] || '').trim()
    };
  }
  return latest;
}

function truncateText(value, max = 120) {
  const text = String(value || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
}

export default function Proposals() {
  const {
    proposals,
    fetchProposals,
    addProposal,
    editProposal,
    reviseProposal,
    removeProposal,
    sendProposal,
    convertProposalToInvoice,
    clients,
    fetchClients,
    leads,
    fetchLeads,
    projectTemplates,
    settings,
    fetchSettings,
    proposalContentTemplates,
    fetchProposalContentTemplates,
    addProposalContentTemplate,
    editProposalContentTemplate,
    removeProposalContentTemplate
  } = useApp();

  const [modalVisible, setModalVisible] = useState(false);
  const [active, setActive] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [estimateSearch, setEstimateSearch] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewRequest, setEmailPreviewRequest] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchProposals();
    fetchClients();
    fetchLeads();
    fetchSettings();
    fetchProposalContentTemplates();
  }, [fetchProposals, fetchClients, fetchLeads, fetchSettings, fetchProposalContentTemplates]);

  const taxRate = Number(settings?.taxRate || 9);

  const summary = useMemo(() => {
    const list = proposals || [];
    const sentCount = list.filter((p) => p.status === 'sent').length;
    const acceptedCount = list.filter((p) => p.status === 'accepted').length;
    const totalValue = list.reduce((sum, p) => sum + Number(p.total || 0), 0);
    return { count: list.length, sentCount, acceptedCount, totalValue };
  }, [proposals]);

  const estimateTotals = useMemo(() => {
    const subtotal = (form.items || []).reduce((sum, item) => sum + (Number(item.rate || 0) * Number(item.quantity || 0)), 0);
    const discount = Math.max(0, Number(form.discount || 0));
    const taxable = Math.max(0, subtotal - discount);
    const tax = Math.round(taxable * (taxRate / 100) * 100) / 100;
    const total = taxable + tax;
    return { subtotal, discount, tax, total };
  }, [form.items, form.discount, taxRate]);

  const selectedEstimateTemplate = form.estimateTemplateKey ? projectTemplates?.[form.estimateTemplateKey] : null;
  const estimateTemplateItems = useMemo(() => {
    if (!selectedEstimateTemplate?.categories) return [];
    return Object.entries(selectedEstimateTemplate.categories).flatMap(([category, items]) =>
      (items || []).map((item) => ({ ...item, category }))
    );
  }, [selectedEstimateTemplate]);
  const filteredEstimateItems = useMemo(() => {
    const term = estimateSearch.toLowerCase().trim();
    if (!term) return estimateTemplateItems;
    return estimateTemplateItems.filter((item) => item.name.toLowerCase().includes(term));
  }, [estimateTemplateItems, estimateSearch]);

  const resetModalState = () => {
    setModalVisible(false);
    setActive(null);
    setIsEditing(false);
    setForm(emptyForm);
    setEstimateSearch('');
    setShowTemplateManager(false);
    setTemplateForm(emptyTemplateForm);
    setEditingTemplateId(null);
  };

  const openCreate = () => {
    setIsEditing(false);
    setActive(null);
    setForm({ ...emptyForm, preparedBy: settings?.companyName || '' });
    setModalVisible(true);
  };

  const openView = (proposal) => {
    const inferredRecipientType = proposal.recipientType || (proposal.client ? 'client' : proposal.lead ? 'lead' : 'manual');
    setActive(proposal);
    setModalVisible(true);
    setIsEditing(false);
    setForm({
      recipientType: inferredRecipientType,
      clientId: proposal.client?._id || proposal.client || '',
      leadId: proposal.lead?._id || proposal.lead || '',
      clientName: proposal.clientName || '',
      clientEmail: proposal.clientEmail || '',
      clientCompany: proposal.clientCompany || proposal.client?.company || proposal.lead?.companyName || '',
      title: proposal.title || '',
      preparedBy: proposal.preparedBy || '',
      executiveSummary: proposal.executiveSummary || '',
      scopeIncludedText: (proposal.scopeIncluded || []).join('\n'),
      scopeExcludedText: (proposal.scopeExcluded || []).join('\n'),
      deliverablesText: (proposal.deliverables || []).join('\n'),
      timelineText: timelineToText(proposal.timeline || []),
      assumptionsText: (proposal.assumptions || []).join('\n'),
      termsPaymentSchedule: proposal.terms?.paymentSchedule || '',
      termsRevisionPolicy: proposal.terms?.revisionPolicy || '',
      termsValidity: proposal.terms?.validity || '',
      termsNextSteps: proposal.terms?.nextSteps || '',
      estimateTemplateKey: proposal.estimateTemplateKey || '',
      contentTemplateKey: proposal.contentTemplateKey || '',
      discount: proposal.discount || 0,
      items: (proposal.items || []).map((i) => ({ ...i })),
      notes: proposal.notes || '',
      expiresAt: proposal.expiresAt ? new Date(proposal.expiresAt).toISOString().slice(0, 10) : ''
    });
  };

  const applyContentTemplate = (key) => {
    if (!key) {
      setForm((prev) => ({ ...prev, contentTemplateKey: '' }));
      return;
    }
    const template = (proposalContentTemplates || []).find((t) => t.key === key);
    if (!template) return;
    setForm((prev) => ({
      ...prev,
      contentTemplateKey: key,
      title: template.title || '',
      executiveSummary: template.executiveSummary || '',
      scopeIncludedText: (template.scopeIncluded || []).join('\n'),
      scopeExcludedText: (template.scopeExcluded || []).join('\n'),
      deliverablesText: (template.deliverables || []).join('\n'),
      timelineText: timelineToText(template.timeline || []),
      assumptionsText: (template.assumptions || []).join('\n'),
      termsPaymentSchedule: template.terms?.paymentSchedule || '',
      termsRevisionPolicy: template.terms?.revisionPolicy || '',
      termsValidity: template.terms?.validity || '',
      termsNextSteps: template.terms?.nextSteps || ''
    }));
  };

  const loadTemplateIntoEditor = (template) => {
    setEditingTemplateId(template._id);
    setTemplateForm({
      key: template.key || '',
      name: template.name || '',
      description: template.description || '',
      title: template.title || '',
      executiveSummary: template.executiveSummary || '',
      scopeIncludedText: (template.scopeIncluded || []).join('\n'),
      scopeExcludedText: (template.scopeExcluded || []).join('\n'),
      deliverablesText: (template.deliverables || []).join('\n'),
      timelineText: timelineToText(template.timeline || []),
      assumptionsText: (template.assumptions || []).join('\n'),
      termsPaymentSchedule: template.terms?.paymentSchedule || '',
      termsRevisionPolicy: template.terms?.revisionPolicy || '',
      termsValidity: template.terms?.validity || '',
      termsNextSteps: template.terms?.nextSteps || '',
      isActive: template.isActive !== false
    });
  };

  const resetTemplateEditor = () => {
    setEditingTemplateId(null);
    setTemplateForm(emptyTemplateForm);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) return alert('Template name is required');
    const payload = {
      key: templateForm.key,
      name: templateForm.name,
      description: templateForm.description,
      title: templateForm.title,
      executiveSummary: templateForm.executiveSummary,
      scopeIncluded: toLines(templateForm.scopeIncludedText),
      scopeExcluded: toLines(templateForm.scopeExcludedText),
      deliverables: toLines(templateForm.deliverablesText),
      timeline: textToTimeline(templateForm.timelineText),
      assumptions: toLines(templateForm.assumptionsText),
      terms: {
        paymentSchedule: templateForm.termsPaymentSchedule,
        revisionPolicy: templateForm.termsRevisionPolicy,
        validity: templateForm.termsValidity,
        nextSteps: templateForm.termsNextSteps
      },
      isActive: templateForm.isActive
    };
    try {
      if (editingTemplateId) {
        await editProposalContentTemplate(editingTemplateId, payload);
      } else {
        await addProposalContentTemplate(payload);
      }
      resetTemplateEditor();
      await fetchProposalContentTemplates();
    } catch (error) {
      alert(error?.response?.data?.error || 'Failed to save template');
    }
  };

  const addLine = () => setForm((f) => ({ ...f, items: [...(f.items || []), { name: '', quantity: 1, rate: 0 }] }));
  const updateLine = (index, patch) =>
    setForm((f) => ({
      ...f,
      items: (f.items || []).map((line, i) => (i === index ? { ...line, ...patch } : line))
    }));
  const removeLine = (index) =>
    setForm((f) => ({
      ...f,
      items: (f.items || []).filter((_, i) => i !== index)
    }));

  const addTemplateLine = (item) => {
    const foundIndex = (form.items || []).findIndex((line) => line.name === item.name);
    if (foundIndex >= 0) {
      updateLine(foundIndex, { quantity: Number(form.items[foundIndex].quantity || 1) + 1 });
      return;
    }
    setForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), { name: item.name, quantity: 1, rate: Number(item.rate || 0), description: item.category || '' }]
    }));
  };

  const toPayload = () => ({
    recipientType: form.recipientType,
    clientId: form.clientId,
    leadId: form.leadId,
    clientName: form.clientName,
    clientEmail: form.clientEmail,
    clientCompany: form.clientCompany,
    title: form.title,
    preparedBy: form.preparedBy,
    executiveSummary: form.executiveSummary,
    scopeIncluded: toLines(form.scopeIncludedText),
    scopeExcluded: toLines(form.scopeExcludedText),
    deliverables: toLines(form.deliverablesText),
    timeline: textToTimeline(form.timelineText),
    assumptions: toLines(form.assumptionsText),
    terms: {
      paymentSchedule: form.termsPaymentSchedule,
      revisionPolicy: form.termsRevisionPolicy,
      validity: form.termsValidity,
      nextSteps: form.termsNextSteps
    },
    estimateTemplateKey: form.estimateTemplateKey,
    contentTemplateKey: form.contentTemplateKey,
    discount: form.discount,
    items: (form.items || []).map((line) => ({
      name: String(line.name || '').trim(),
      quantity: Math.max(1, Number(line.quantity || 1)),
      rate: Math.max(0, Number(line.rate || 0)),
      description: String(line.description || '').trim()
    })).filter((line) => line.name),
    notes: form.notes,
    expiresAt: form.expiresAt
  });

  const save = async () => {
    if (form.recipientType === 'client' && !form.clientId) return alert('Client is required.');
    if (form.recipientType === 'lead' && !form.leadId) return alert('Lead is required.');
    if (form.recipientType === 'manual' && (!String(form.clientName || '').trim() || !String(form.clientEmail || '').trim())) {
      return alert('Recipient name and email are required.');
    }
    const payload = toPayload();
    if (!payload.items.length) return alert('Add at least one estimate line item.');
    try {
      if (active && isEditing) {
        if ((active.status || 'draft') !== 'draft') {
          const revised = await reviseProposal(active._id, payload);
          const newProposalNumber = revised?.proposal?.proposalNumber || revised?.proposal?._id || 'draft revision';
          alert(`Sent proposal is immutable. Created ${newProposalNumber} as a new draft revision.`);
        } else {
          await editProposal(active._id, payload);
          alert('Proposal updated');
        }
      } else {
        await addProposal(payload);
        alert('Proposal created');
      }
      resetModalState();
      await fetchProposals();
    } catch (error) {
      console.error(error);
      alert('Failed to save proposal');
    }
  };

  const handlePreviewProposal = () => {
    const payload = toPayload();
    const selectedClient = (clients || []).find((c) => c._id === payload.clientId);
    const selectedLead = (leads || []).find((l) => l._id === payload.leadId);
    const html = buildProposalHTML({
      ...payload,
      proposalNumber: active?.proposalNumber || 'PREVIEW',
      clientName: selectedClient?.name || selectedLead?.companyName || payload.clientName || active?.clientName || '',
      clientEmail: selectedClient?.email || selectedLead?.contactEmail || payload.clientEmail || active?.clientEmail || '',
      clientCompany: selectedClient?.company || selectedLead?.companyName || payload.clientCompany || active?.client?.company || '',
      subtotal: estimateTotals.subtotal,
      discount: estimateTotals.discount,
      tax: estimateTotals.tax,
      total: estimateTotals.total,
      status: active?.status || 'draft'
    }, settings || {});
    const popup = window.open('', '_blank');
    if (!popup) return alert('Popup blocked. Please allow popups and try again.');
    popup.document.write(html);
    popup.document.close();
  };

  const statusClass = (status) => `proposal-status proposal-status--${status || 'draft'}`;

  const openSendPreview = (proposal) => {
    const apiBase = (process.env.REACT_APP_API_URL || 'https://api.pagehyve.com/api').replace(/\/$/, '');
    setEmailPreviewRequest({
      type: 'proposal',
      clientName: proposal.clientName,
      clientEmail: proposal.clientEmail,
      proposalNumber: proposal.proposalNumber,
      total: proposal.total,
      expiresAt: proposal.expiresAt ? new Date(proposal.expiresAt).toLocaleDateString() : '',
      proposalLink: `${apiBase}/proposals/${proposal._id}/public`
    });
    window.__emailPreviewProposalId = proposal._id;
    setEmailPreviewOpen(true);
  };

  const activeDeclineNote = useMemo(() => getLatestDeclineNote(active?.notes), [active?.notes]);

  return (
    <div className="proposals-page">
      <section className="proposals-hero">
        <div>
          <h2>Proposals</h2>
          <p>Build complete proposals with narrative, scope, timeline, and estimate-backed pricing.</p>
        </div>
        <button className="proposal-btn proposal-btn--primary" onClick={openCreate}>
          <Plus size={16} /> New Proposal
        </button>
      </section>

      <section className="proposals-metrics">
        <article><span>Total Proposals</span><strong>{summary.count}</strong></article>
        <article><span>Sent</span><strong>{summary.sentCount}</strong></article>
        <article><span>Accepted</span><strong>{summary.acceptedCount}</strong></article>
        <article><span>Pipeline Value</span><strong>{money(summary.totalValue)}</strong></article>
      </section>

      <section className="proposals-table-card responsive-table">
        <table>
          <thead>
            <tr>
              <th>Proposal</th>
              <th>Client</th>
              <th>Expires</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(proposals || []).map((proposal) => {
              const latestDecline = getLatestDeclineNote(proposal.notes);
              return (
                <tr key={proposal._id}>
                  <td data-label="Proposal">
                    <button className="proposal-link" onClick={() => openView(proposal)}>
                      {proposal.proposalNumber}
                    </button>
                  </td>
                  <td data-label="Client">{proposal.clientName}</td>
                  <td data-label="Expires">{proposal.expiresAt ? new Date(proposal.expiresAt).toLocaleDateString() : 'No expiry'}</td>
                  <td data-label="Amount">{money(proposal.total)}</td>
                  <td data-label="Status">
                    <span className={statusClass(proposal.status)}>{proposal.status || 'draft'}</span>
                    {latestDecline ? (
                      <div
                        className="proposal-decline-snippet"
                        title={latestDecline.reason}
                      >
                        Decline: {truncateText(latestDecline.reason, 80)}
                      </div>
                    ) : null}
                  </td>
                  <td data-label="Actions">
                    <div className="proposal-actions">
                      <button className="proposal-btn proposal-btn--ghost" onClick={() => openView(proposal)}><Eye size={14} /> View</button>
                      <button
                        className="proposal-btn proposal-btn--send"
                        onClick={() => openSendPreview(proposal)}
                      >
                        <Mail size={14} /> Send
                      </button>
                      <button
                        className="proposal-btn proposal-btn--convert"
                        onClick={async () => {
                          try {
                            const result = await convertProposalToInvoice(proposal._id);
                            alert(`Converted to invoice ${result.invoice.invoiceNumber || result.invoice._id}`);
                            await fetchProposals();
                          } catch (error) {
                            alert('Failed to convert');
                          }
                        }}
                      >
                        <ArrowRightLeft size={14} /> Convert
                      </button>
                      <button
                        className="proposal-btn proposal-btn--danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this proposal?')) return;
                          await removeProposal(proposal._id);
                          await fetchProposals();
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {modalVisible && (
        <div className="proposal-modal-overlay" onClick={resetModalState}>
          <div className="proposal-modal proposal-modal--xl" onClick={(e) => e.stopPropagation()}>
            <header className="proposal-modal-header">
              <div>
                <h3>{active ? (isEditing ? 'Edit Proposal' : 'Proposal Details') : 'Create Proposal'}</h3>
                <p>{active?.proposalNumber || 'Proposal content + estimate section'}</p>
              </div>
              <button className="proposal-btn proposal-btn--icon" onClick={resetModalState}><X size={18} /></button>
            </header>

            {active && !isEditing ? (
              <div className="proposal-detail">
                <div className="proposal-detail-top">
                  <div>
                    <span className={statusClass(active.status)}>{active.status}</span>
                    <h4>{active.title || active.proposalNumber}</h4>
                    <p>{active.clientName} - {active.clientEmail}</p>
                  </div>
                  <div className="proposal-detail-total">{money(active.total)}</div>
                </div>

                {active.executiveSummary ? (
                  <div className="proposal-notes">
                    <h5>Executive Summary</h5>
                    <p>{active.executiveSummary}</p>
                  </div>
                ) : null}

                {activeDeclineNote ? (
                  <div className="proposal-notes proposal-notes--decline">
                    <h5>Latest Client Decline Note</h5>
                    <p>{activeDeclineNote.reason}</p>
                    <small>{activeDeclineNote.when}</small>
                  </div>
                ) : null}

                <div className="proposal-section-grid">
                  <div className="proposal-notes">
                    <h5>Scope Included</h5>
                    <p>{(active.scopeIncluded || []).join('\n') || 'Not provided'}</p>
                  </div>
                  <div className="proposal-notes">
                    <h5>Deliverables</h5>
                    <p>{(active.deliverables || []).join('\n') || 'Not provided'}</p>
                  </div>
                </div>

                <div className="proposal-line-items">
                  {(active.items || []).map((item, i) => (
                    <div key={`${item.name}-${i}`} className="proposal-line-item">
                      <span>{item.name}</span>
                      <span>{item.quantity} x {money(item.rate)}</span>
                      <strong>{money(Number(item.quantity) * Number(item.rate))}</strong>
                    </div>
                  ))}
                </div>

                <div className="proposal-estimate-summary">
                  <span>Subtotal: {money(active.subtotal)}</span>
                  <span>Discount: {money(active.discount)}</span>
                  <span>Tax: {money(active.tax)}</span>
                  <strong>Total: {money(active.total)}</strong>
                </div>

                <div className="proposal-modal-actions">
                  <button className="proposal-btn proposal-btn--ghost" onClick={handlePreviewProposal}><Eye size={14} /> Preview</button>
                  <button className="proposal-btn proposal-btn--primary" onClick={() => setIsEditing(true)}><Pencil size={14} /> Edit</button>
                  <button
                    className="proposal-btn proposal-btn--convert"
                    onClick={async () => {
                      try {
                        await convertProposalToInvoice(active._id);
                        await fetchProposals();
                        resetModalState();
                        alert('Converted to invoice');
                      } catch (error) {
                        alert('Failed to convert');
                      }
                    }}
                  >
                    <ArrowRightLeft size={14} /> Convert
                  </button>
                  <button
                    className="proposal-btn proposal-btn--send"
                    onClick={() => openSendPreview(active)}
                  >
                    <Mail size={14} /> Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="proposal-editor">
                <div className="proposal-editor-grid proposal-editor-grid--three">
                  <label>
                    Recipient Type
                    <select
                      value={form.recipientType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          recipientType: e.target.value,
                          clientId: e.target.value === 'client' ? prev.clientId : '',
                          leadId: e.target.value === 'lead' ? prev.leadId : '',
                        }))
                      }
                    >
                      <option value="lead">Lead / Prospect</option>
                      <option value="client">Existing Client</option>
                      <option value="manual">Manual Contact</option>
                    </select>
                  </label>
                  {form.recipientType === 'client' ? (
                    <label>
                      Client
                      <select
                        value={form.clientId}
                        onChange={(e) => {
                          const client = (clients || []).find((c) => c._id === e.target.value);
                          setForm((prev) => ({
                            ...prev,
                            clientId: e.target.value,
                            clientName: client?.name || prev.clientName,
                            clientEmail: client?.email || prev.clientEmail,
                            clientCompany: client?.company || prev.clientCompany,
                          }));
                        }}
                      >
                        <option value="">Choose client...</option>
                        {(clients || []).map((client) => (
                          <option key={client._id} value={client._id}>{client.name} {client.company ? `- ${client.company}` : ''}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {form.recipientType === 'lead' ? (
                    <label>
                      Lead / Prospect
                      <select
                        value={form.leadId}
                        onChange={(e) => {
                          const lead = (leads || []).find((l) => l._id === e.target.value);
                          setForm((prev) => ({
                            ...prev,
                            leadId: e.target.value,
                            clientName: lead?.companyName || prev.clientName,
                            clientEmail: lead?.contactEmail || prev.clientEmail,
                            clientCompany: lead?.companyName || prev.clientCompany,
                          }));
                        }}
                      >
                        <option value="">Choose lead...</option>
                        {(leads || []).map((lead) => (
                          <option key={lead._id} value={lead._id}>
                            {lead.companyName} {lead.contactEmail ? `- ${lead.contactEmail}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {form.recipientType === 'manual' ? (
                    <label>
                      Recipient Name
                      <input value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} placeholder="Prospect or company name" />
                    </label>
                  ) : null}
                  <label>
                    Expires
                    <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                  </label>
                  <label>
                    Prepared By
                    <input value={form.preparedBy} onChange={(e) => setForm({ ...form, preparedBy: e.target.value })} placeholder="Account manager or company name" />
                  </label>
                </div>
                <div className="proposal-section-grid">
                  <label>
                    Recipient Email
                    <input value={form.clientEmail} onChange={(e) => setForm({ ...form, clientEmail: e.target.value })} placeholder="email@company.com" />
                  </label>
                  <label>
                    Recipient Company
                    <input value={form.clientCompany} onChange={(e) => setForm({ ...form, clientCompany: e.target.value })} placeholder="Company name" />
                  </label>
                </div>

                <label>
                  Proposal Title
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Example: Website Redesign and SEO Proposal" />
                </label>

                <div className="proposal-template-row">
                  <label>
                    Content Template
                    <select value={form.contentTemplateKey} onChange={(e) => applyContentTemplate(e.target.value)}>
                      <option value="">Custom</option>
                      {(proposalContentTemplates || []).filter((t) => t.isActive !== false).map((template) => (
                        <option key={template._id} value={template.key}>{template.name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Estimate Template
                    <select value={form.estimateTemplateKey} onChange={(e) => setForm({ ...form, estimateTemplateKey: e.target.value })}>
                      <option value="">Custom</option>
                      {Object.entries(projectTemplates || {}).map(([key, template]) => (
                        <option key={key} value={key}>{template.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="proposal-template-controls">
                  <button className="proposal-btn proposal-btn--ghost" onClick={() => setShowTemplateManager((v) => !v)}>
                    {showTemplateManager ? 'Hide Template Manager' : 'Manage Content Templates'}
                  </button>
                </div>

                {showTemplateManager ? (
                  <div className="proposal-template-manager">
                    <div className="proposal-template-manager-list">
                      <h4>Saved Content Templates</h4>
                      {(proposalContentTemplates || []).map((template) => (
                        <div key={template._id} className="proposal-template-manager-item">
                          <div>
                            <strong>{template.name}</strong>
                            <p>{template.key} {template.isActive === false ? '(inactive)' : ''}</p>
                          </div>
                          <div className="proposal-template-manager-actions">
                            <button className="proposal-btn proposal-btn--ghost" onClick={() => applyContentTemplate(template.key)}>Use</button>
                            <button className="proposal-btn proposal-btn--ghost" onClick={() => loadTemplateIntoEditor(template)}>Edit</button>
                            <button
                              className="proposal-btn proposal-btn--danger"
                              onClick={async () => {
                                if (!window.confirm(`Delete template "${template.name}"?`)) return;
                                try {
                                  await removeProposalContentTemplate(template._id);
                                  if (form.contentTemplateKey === template.key) setForm((prev) => ({ ...prev, contentTemplateKey: '' }));
                                  await fetchProposalContentTemplates();
                                } catch (error) {
                                  alert(error?.response?.data?.error || 'Failed to delete template');
                                }
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="proposal-template-manager-editor">
                      <h4>{editingTemplateId ? 'Edit Template' : 'New Template'}</h4>
                      <label>
                        Name
                        <input value={templateForm.name} onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))} />
                      </label>
                      <label>
                        Key
                        <input value={templateForm.key} onChange={(e) => setTemplateForm((prev) => ({ ...prev, key: e.target.value }))} placeholder="auto-generated-if-empty" />
                      </label>
                      <label>
                        Description
                        <input value={templateForm.description} onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))} />
                      </label>
                      <label>
                        Title
                        <input value={templateForm.title} onChange={(e) => setTemplateForm((prev) => ({ ...prev, title: e.target.value }))} />
                      </label>
                      <label>
                        Executive Summary
                        <textarea rows="3" value={templateForm.executiveSummary} onChange={(e) => setTemplateForm((prev) => ({ ...prev, executiveSummary: e.target.value }))} />
                      </label>
                      <div className="proposal-section-grid">
                        <label>
                          Scope Included (one per line)
                          <textarea rows="3" value={templateForm.scopeIncludedText} onChange={(e) => setTemplateForm((prev) => ({ ...prev, scopeIncludedText: e.target.value }))} />
                        </label>
                        <label>
                          Scope Excluded (one per line)
                          <textarea rows="3" value={templateForm.scopeExcludedText} onChange={(e) => setTemplateForm((prev) => ({ ...prev, scopeExcludedText: e.target.value }))} />
                        </label>
                      </div>
                      <div className="proposal-section-grid">
                        <label>
                          Deliverables (one per line)
                          <textarea rows="3" value={templateForm.deliverablesText} onChange={(e) => setTemplateForm((prev) => ({ ...prev, deliverablesText: e.target.value }))} />
                        </label>
                        <label>
                          Assumptions (one per line)
                          <textarea rows="3" value={templateForm.assumptionsText} onChange={(e) => setTemplateForm((prev) => ({ ...prev, assumptionsText: e.target.value }))} />
                        </label>
                      </div>
                      <label>
                        Timeline (`Phase | Duration | Details`)
                        <textarea rows="3" value={templateForm.timelineText} onChange={(e) => setTemplateForm((prev) => ({ ...prev, timelineText: e.target.value }))} />
                      </label>
                      <div className="proposal-section-grid">
                        <label>
                          Terms: Payment Schedule
                          <textarea rows="2" value={templateForm.termsPaymentSchedule} onChange={(e) => setTemplateForm((prev) => ({ ...prev, termsPaymentSchedule: e.target.value }))} />
                        </label>
                        <label>
                          Terms: Revision Policy
                          <textarea rows="2" value={templateForm.termsRevisionPolicy} onChange={(e) => setTemplateForm((prev) => ({ ...prev, termsRevisionPolicy: e.target.value }))} />
                        </label>
                      </div>
                      <div className="proposal-section-grid">
                        <label>
                          Terms: Validity
                          <textarea rows="2" value={templateForm.termsValidity} onChange={(e) => setTemplateForm((prev) => ({ ...prev, termsValidity: e.target.value }))} />
                        </label>
                        <label>
                          Terms: Next Steps
                          <textarea rows="2" value={templateForm.termsNextSteps} onChange={(e) => setTemplateForm((prev) => ({ ...prev, termsNextSteps: e.target.value }))} />
                        </label>
                      </div>
                      <label className="proposal-checkbox">
                        <input type="checkbox" checked={templateForm.isActive} onChange={(e) => setTemplateForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                        Active template
                      </label>
                      <div className="proposal-modal-actions">
                        {editingTemplateId ? (
                          <button className="proposal-btn proposal-btn--ghost" onClick={resetTemplateEditor}>New Template</button>
                        ) : null}
                        <button className="proposal-btn proposal-btn--primary" onClick={saveTemplate}>Save Template</button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <label>
                  Executive Summary
                  <textarea rows="4" value={form.executiveSummary} onChange={(e) => setForm({ ...form, executiveSummary: e.target.value })} placeholder="Business context, objective, and intended outcomes..." />
                </label>

                <div className="proposal-section-grid">
                  <label>
                    Scope Included (one per line)
                    <textarea rows="4" value={form.scopeIncludedText} onChange={(e) => setForm({ ...form, scopeIncludedText: e.target.value })} />
                  </label>
                  <label>
                    Scope Excluded (one per line)
                    <textarea rows="4" value={form.scopeExcludedText} onChange={(e) => setForm({ ...form, scopeExcludedText: e.target.value })} />
                  </label>
                </div>

                <div className="proposal-section-grid">
                  <label>
                    Deliverables (one per line)
                    <textarea rows="4" value={form.deliverablesText} onChange={(e) => setForm({ ...form, deliverablesText: e.target.value })} />
                  </label>
                  <label>
                    Assumptions & Dependencies (one per line)
                    <textarea rows="4" value={form.assumptionsText} onChange={(e) => setForm({ ...form, assumptionsText: e.target.value })} />
                  </label>
                </div>

                <label>
                  Timeline (one row per line: `Phase | Duration | Details`)
                  <textarea rows="4" value={form.timelineText} onChange={(e) => setForm({ ...form, timelineText: e.target.value })} placeholder="Discovery | 1 week | Requirements and alignment" />
                </label>

                <div className="proposal-section-grid">
                  <label>
                    Terms: Payment Schedule
                    <textarea rows="3" value={form.termsPaymentSchedule} onChange={(e) => setForm({ ...form, termsPaymentSchedule: e.target.value })} />
                  </label>
                  <label>
                    Terms: Revision Policy
                    <textarea rows="3" value={form.termsRevisionPolicy} onChange={(e) => setForm({ ...form, termsRevisionPolicy: e.target.value })} />
                  </label>
                </div>

                <div className="proposal-section-grid">
                  <label>
                    Terms: Validity
                    <textarea rows="2" value={form.termsValidity} onChange={(e) => setForm({ ...form, termsValidity: e.target.value })} />
                  </label>
                  <label>
                    Terms: Next Steps
                    <textarea rows="2" value={form.termsNextSteps} onChange={(e) => setForm({ ...form, termsNextSteps: e.target.value })} />
                  </label>
                </div>

                <div className="proposal-editor-items">
                  <div className="proposal-editor-items-header">
                    <h4>Estimate Section</h4>
                    <button className="proposal-btn proposal-btn--primary" onClick={addLine}><Plus size={14} /> Add Manual Line</button>
                  </div>

                  {selectedEstimateTemplate ? (
                    <div className="proposal-template-items">
                      <div className="proposal-template-items-head">
                        <Search size={14} />
                        <input value={estimateSearch} onChange={(e) => setEstimateSearch(e.target.value)} placeholder="Search template line items..." />
                      </div>
                      <div className="proposal-template-items-list">
                        {filteredEstimateItems.slice(0, 120).map((item) => (
                          <button key={`${item.category}-${item.name}`} className="proposal-template-item" onClick={() => addTemplateLine(item)}>
                            <span>{item.name}</span>
                            <strong>${Number(item.rate || 0).toFixed(2)}</strong>
                          </button>
                        ))}
                        {filteredEstimateItems.length === 0 ? <p>No template items found.</p> : null}
                      </div>
                    </div>
                  ) : null}

                  {(form.items || []).map((item, i) => (
                    <div key={`line-${i}`} className="proposal-editor-line">
                      <input value={item.name} onChange={(e) => updateLine(i, { name: e.target.value })} placeholder="Description" />
                      <input type="number" min="1" value={item.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) || 1 })} placeholder="Qty" />
                      <input type="number" min="0" value={item.rate} onChange={(e) => updateLine(i, { rate: Number(e.target.value) || 0 })} placeholder="Rate" />
                      <div className="proposal-editor-line-total">{money(Number(item.quantity) * Number(item.rate))}</div>
                      <button className="proposal-btn proposal-btn--danger" onClick={() => removeLine(i)}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>

                <div className="proposal-section-grid">
                  <label>
                    Discount
                    <input type="number" min="0" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
                  </label>
                  <label>
                    Additional Notes
                    <textarea rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </label>
                </div>

                <div className="proposal-editor-summary">
                  <span>Subtotal: {money(estimateTotals.subtotal)}</span>
                  <span>Discount: {money(estimateTotals.discount)}</span>
                  <span>Tax ({taxRate}%): {money(estimateTotals.tax)}</span>
                  <strong>Total: {money(estimateTotals.total)}</strong>
                </div>

                <div className="proposal-modal-actions">
                  <button className="proposal-btn proposal-btn--ghost" onClick={resetModalState}>Cancel</button>
                  <button className="proposal-btn proposal-btn--ghost" onClick={handlePreviewProposal}><Eye size={14} /> Preview</button>
                  <button className="proposal-btn proposal-btn--primary" onClick={save}>Save Proposal</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <EmailPreviewModal
        isOpen={emailPreviewOpen}
        onClose={() => {
          if (sendingEmail) return;
          setEmailPreviewOpen(false);
          setEmailPreviewRequest(null);
          window.__emailPreviewProposalId = null;
        }}
        onConfirm={async (customHtml) => {
          setSendingEmail(true);
          try {
            const id = window.__emailPreviewProposalId;
            await sendProposal(id, customHtml);
            await fetchProposals();
            alert('Proposal sent successfully');
          } catch (error) {
            console.error('Failed to send proposal', error);
            alert('Failed to send proposal');
          } finally {
            setSendingEmail(false);
            setEmailPreviewOpen(false);
            window.__emailPreviewProposalId = null;
            setEmailPreviewRequest(null);
          }
        }}
        clientName={emailPreviewRequest?.clientName}
        clientEmail={emailPreviewRequest?.clientEmail}
        previewRequest={emailPreviewRequest}
        isLoading={sendingEmail}
      />
    </div>
  );
}
